import axios from 'axios';

// Normalize API_URL to remove trailing /api to prevent double /api/api
const normalizeApiUrl = (url) => {
  if (!url) return 'http://localhost:5000';
  // Remove trailing slash
  url = url.replace(/\/$/, '');
  // Remove trailing /api if present
  url = url.replace(/\/api$/, '');
  return url;
};

const API_URL = normalizeApiUrl(process.env.REACT_APP_API_URL || 'http://localhost:5000');

class AnalyticsService {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.eventQueue = [];
    this.batchSize = 10;
    this.flushDelayMs = 3000; // 3 seconds delay for batching
    this.flushTimeoutId = null;
    this.isTracking = true;
    
    // Track session end on page unload
    window.addEventListener('beforeunload', () => this.endSession());
  }

  /**
   * Generate or retrieve session ID
   */
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem('analytics_session_id', sessionId);
      
      // Store session start time
      sessionStorage.setItem('analytics_session_start', Date.now().toString());
    }
    return sessionId;
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get device information
   */
  getDeviceInfo() {
    const ua = navigator.userAgent;
    let deviceType = 'Desktop';
    
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      deviceType = 'Tablet';
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      deviceType = 'Mobile';
    }
    
    // Extract browser and OS
    let browser = 'Unknown';
    let os = 'Unknown';
    
    if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (ua.indexOf('Safari') > -1) browser = 'Safari';
    else if (ua.indexOf('Edge') > -1) browser = 'Edge';
    
    if (ua.indexOf('Win') > -1) os = 'Windows';
    else if (ua.indexOf('Mac') > -1) os = 'MacOS';
    else if (ua.indexOf('Linux') > -1) os = 'Linux';
    else if (ua.indexOf('Android') > -1) os = 'Android';
    else if (ua.indexOf('iOS') > -1) os = 'iOS';
    
    return { deviceType, browser, os };
  }

  /**
   * Get UTM parameters from URL
   */
  getUtmParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
    };
  }

  /**
   * Track an event
   */
  trackEvent(eventType, eventData = {}) {
    if (!this.isTracking) return;
    
    const event = {
      session_id: this.sessionId,
      event_type: eventType,
      event_data: eventData,
      page_url: window.location.pathname + window.location.search,
      timestamp: new Date().toISOString(),
    };
    
    this.eventQueue.push(event);
    
    // Send immediately if queue is full
    if (this.eventQueue.length >= this.batchSize) {
      this.cancelScheduledFlush();
      this.sendBatch();
    } else {
      // Schedule a flush if not already scheduled
      this.scheduleFlush();
    }
  }

  /**
   * Schedule a single flush after delay (action-based batching)
   */
  scheduleFlush() {
    // If a flush is already scheduled, don't schedule another
    if (this.flushTimeoutId !== null) {
      return;
    }

    // Schedule flush after delay
    this.flushTimeoutId = setTimeout(() => {
      this.flushTimeoutId = null;
      this.sendBatch();
    }, this.flushDelayMs);
  }

  /**
   * Cancel any scheduled flush
   */
  cancelScheduledFlush() {
    if (this.flushTimeoutId !== null) {
      clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = null;
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName, additionalData = {}) {
    this.trackEvent('page_view', {
      page_name: pageName,
      referrer: document.referrer,
      ...additionalData,
    });
  }

  /**
   * Track product view
   */
  trackProductView(productId, productName, categoryId, categoryName, price) {
    this.trackEvent('product_view', {
      product_id: productId,
      product_name: productName,
      category_id: categoryId,
      category_name: categoryName,
      price: price,
      time_on_page: this.getTimeOnPage(),
    });
  }

  /**
   * Track search
   */
  trackSearch(query, resultsCount = 0) {
    this.trackEvent('search', {
      query: query,
      results_count: resultsCount,
    });
  }

  /**
   * Track add to cart
   */
  trackAddToCart(productId, productName, quantity, price) {
    this.trackEvent('add_to_cart', {
      product_id: productId,
      product_name: productName,
      quantity: quantity,
      price: price,
    });
  }

  /**
   * Track remove from cart
   */
  trackRemoveFromCart(productId, productName) {
    this.trackEvent('remove_from_cart', {
      product_id: productId,
      product_name: productName,
    });
  }

  /**
   * Track checkout started
   */
  trackCheckoutStarted(cartTotal, itemCount) {
    this.trackEvent('checkout_started', {
      cart_total: cartTotal,
      item_count: itemCount,
    });
  }

  /**
   * Track purchase
   */
  trackPurchase(orderId, orderTotal, itemCount) {
    this.trackEvent('purchase', {
      order_id: orderId,
      order_total: orderTotal,
      item_count: itemCount,
    });
  }

  /**
   * Track category view
   */
  trackCategoryView(categoryId, categoryName) {
    this.trackEvent('category_view', {
      category_id: categoryId,
      category_name: categoryName,
    });
  }

  /**
   * Track wishlist add
   */
  trackWishlistAdd(productId, productName) {
    this.trackEvent('wishlist_add', {
      product_id: productId,
      product_name: productName,
    });
  }

  /**
   * Track filter usage
   */
  trackFilterUsage(filterType, filterValue) {
    this.trackEvent('filter_used', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  }

  /**
   * Get time spent on current page
   */
  getTimeOnPage() {
    const startTime = parseInt(sessionStorage.getItem('analytics_page_start') || Date.now());
    return Math.floor((Date.now() - startTime) / 1000); // in seconds
  }

  /**
   * Start tracking current page
   */
  startPageTracking() {
    sessionStorage.setItem('analytics_page_start', Date.now().toString());
  }

  /**
   * Send batched events to backend (fire-and-forget, no await)
   */
  sendBatch() {
    if (this.eventQueue.length === 0) return;
    
    const events = [...this.eventQueue];
    this.eventQueue = [];
    
    // Fire and forget - do not await
    axios.post(`${API_URL}/api/client/analytics/batch`, {
      events: events,
      device_info: this.getDeviceInfo(),
      utm_params: this.getUtmParams(),
      referrer: document.referrer,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Include cookies for authentication
    })
    .catch((error) => {
      console.error('Failed to send analytics batch:', error);
      // Re-add events to queue on failure (with limit to prevent memory issues)
      // Only retry on next user activity (via scheduleFlush)
      if (this.eventQueue.length < 100) {
        this.eventQueue.unshift(...events);
        // Schedule a retry flush
        this.scheduleFlush();
      }
    });
  }

  /**
   * End session tracking
   */
  endSession() {
    // Cancel any scheduled flush
    this.cancelScheduledFlush();
    
    // Send any remaining events immediately
    this.sendBatch();
    
    // Track session end
    const sessionStart = parseInt(sessionStorage.getItem('analytics_session_start') || Date.now());
    const duration = Math.floor((Date.now() - sessionStart) / 1000);
    
    // Use sendBeacon for reliability during page unload
    if (navigator.sendBeacon) {
      const data = JSON.stringify({
        session_id: this.sessionId,
        event_type: 'session_end',
        event_data: {
          duration_seconds: duration,
        },
        page_url: window.location.pathname,
        device_info: this.getDeviceInfo(),
        utm_params: this.getUtmParams(),
      });
      
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/api/client/analytics/track`, blob);
    } else {
      // Fallback to synchronous request (not recommended but necessary)
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/client/analytics/track`, false); // synchronous
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(JSON.stringify({
          session_id: this.sessionId,
          event_type: 'session_end',
          event_data: {
            duration_seconds: duration,
          },
          page_url: window.location.pathname,
          device_info: this.getDeviceInfo(),
          utm_params: this.getUtmParams(),
        }));
      } catch (error) {
        console.debug('Session end tracking failed:', error);
      }
    }
  }

  /**
   * Initialize session tracking
   */
  initSession() {
    const { deviceType, browser, os } = this.getDeviceInfo();
    const utmParams = this.getUtmParams();
    
    this.trackEvent('session_start', {
      device_type: deviceType,
      browser: browser,
      os: os,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      referrer: document.referrer,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
    });
  }

  /**
   * Disable tracking (for opt-out)
   */
  disableTracking() {
    this.isTracking = false;
  }

  /**
   * Enable tracking
   */
  enableTracking() {
    this.isTracking = true;
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
